import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, MessageCircle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Üslü sayılarda negatif kuvvet nasıl çalışır?",
  "Pisagor bağıntısını günlük hayatta nerede kullanırız?",
  "Fiilimsileri kolay tanımanın yolu nedir?",
  "EBOB ve EKOK farkını basitçe anlatır mısın?",
];

export default function AITeacherPage() {
  const [search] = useSearchParams();
  const initialTopic = search.get("topic");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const token = localStorage.getItem("ela_token");
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text, topic_code: initialTopic }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d.delta };
                return c;
              });
            } else if (d.done) {
              setSessionId(d.session_id);
            }
          } catch {}
        }
      }
    } catch (e) {
      toast.error("Bağlantı hatası");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-4 ela-fade-in flex flex-col h-[calc(100vh-180px)]" data-testid="ai-teacher-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <MessageCircle className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">AI Öğretmen</h1>
          <p className="text-xs text-slate-500">Claude Sonnet 4.5 · Sana özel anlatım</p>
        </div>
      </div>

      <Card className="flex-1 rounded-2xl border-slate-200 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <Sparkles className="w-10 h-10 text-[#FF6B6B] mb-3" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Sohbete başla</h3>
              <p className="text-sm text-slate-500 mt-1">Aklındaki her soruyu sorabilirsin.</p>
              <div className="mt-5 grid gap-2 w-full max-w-md">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)}
                    data-testid={`teacher-suggestion-${i}`}
                    className="text-left text-sm p-3 rounded-xl border border-slate-200 hover:border-[#FF6B6B] hover:bg-rose-50/30 text-slate-700 transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-w-3xl mx-auto">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#FF6B6B] text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}>
                    {m.content || (streaming && i === messages.length - 1 && <span className="ela-pulse text-slate-400">yazıyor…</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-slate-200 p-3 bg-slate-50">
          <div className="flex gap-2 max-w-3xl mx-auto">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="teacher-input"
              placeholder="Aklındaki soruyu yaz…"
              disabled={streaming}
              className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#FF6B6B] focus:ring-2 focus:ring-rose-100"
            />
            <Button type="submit" disabled={streaming || !input.trim()}
              data-testid="teacher-send-btn"
              className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-xl px-4">
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
